/**
 * Guards the global search / entity dropdown queries against drift between the
 * persisted entity definition (Firestore) and the dataset definition that backs
 * the GraphQL/Elastic layer.
 *
 * An entity definition stores a `primaryField` plus `secondaryFields` and
 * `contextFields`. Over time a customer's dataset evolves and fields get
 * renamed or removed, leaving the persisted entity referencing fields that no
 * longer exist. Sending those fields in an `aggregateCompoundMetric` query
 * causes the backend to reject the whole request.
 *
 * This validates the entity's fields against the dataset definition so the
 * caller can prune stale secondary/context fields and skip an entity entirely
 * when its primary field is gone (a query grouped by / wildcarded on a
 * non-existent field can never succeed).
 *
 * Fails open: when the dataset definition can't be found we return everything
 * unchanged rather than over-pruning — callers still guard against a failing
 * query so nothing hangs.
 */
const getSanitizedEntityFields = ({
  entity,
  datasetDefinition,
}: {
  entity: EntityDetails.Entity;
  datasetDefinition: FleetOps.DatasetDefinition | undefined;
}): {
  isPrimaryFieldValid: boolean;
  secondaryFields: string[];
  contextFields: string[];
} => {
  if (!datasetDefinition) {
    return {
      isPrimaryFieldValid: true,
      secondaryFields: entity.secondaryFields,
      contextFields: entity.contextFields,
    };
  }

  const validFields = new Set(datasetDefinition.fields.map((f) => f.field));

  return {
    isPrimaryFieldValid: validFields.has(entity.primaryField),
    secondaryFields: entity.secondaryFields.filter((f) => validFields.has(f)),
    contextFields: entity.contextFields.filter((f) => validFields.has(f)),
  };
};

export default getSanitizedEntityFields;
